<template>
  <div class="upload-card" :class="{ 'upload-card--failed': isFailed }">
    <div class="upload-card__header">
      <div class="upload-card__icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17,8 12,3 7,8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>
      <div class="upload-card__info">
        <span class="upload-card__filename">{{ task.fileName }}</span>
        <span class="upload-card__percentage">{{ progressPercent }}%</span>
      </div>
    </div>

    <div class="upload-card__progress">
      <div
        class="upload-card__progress-bar"
        :style="{ width: `${progressPercent}%` }"
      ></div>
    </div>

    <div v-if="task.totalBytes > 0" class="upload-card__details">
      {{ uploadedMB }} / {{ totalMB }} MB
    </div>

    <div v-if="isFailed" class="upload-card__error">
      <div class="upload-card__error-icon">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <span class="upload-card__error-message">{{
        task.errorMessage || "Upload failed"
      }}</span>
      <button class="upload-card__retry-btn" @click="$emit('retry', task.id)">
        Retry
      </button>
    </div>

    <button
      v-if="!isFailed && isUploading"
      class="upload-card__cancel-btn"
      @click="$emit('cancel', task.id)"
    >
      Cancel
    </button>
  </div>
</template>

<script>
import { computed } from "vue";
import { MiaUploadStatus } from "../../types";

export default {
  name: "ActiveUploadCard",
  props: {
    task: {
      type: Object,
      required: true,
    },
  },
  emits: ["retry", "cancel"],
  setup(props) {
    const progressPercent = computed(() => {
      return Math.round(props.task.progress * 100);
    });

    const uploadedMB = computed(() => {
      return (props.task.uploadedBytes / (1024 * 1024)).toFixed(1);
    });

    const totalMB = computed(() => {
      return (props.task.totalBytes / (1024 * 1024)).toFixed(1);
    });

    const isFailed = computed(() => {
      return props.task.status === MiaUploadStatus.FAILED;
    });

    const isUploading = computed(() => {
      return props.task.status === MiaUploadStatus.UPLOADING;
    });

    return {
      progressPercent,
      uploadedMB,
      totalMB,
      isFailed,
      isUploading,
    };
  },
};
</script>

<style scoped>
.upload-card {
  padding: 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  margin-bottom: 8px;
}

.upload-card--failed {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.upload-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.upload-card__icon {
  color: #6366f1;
  display: flex;
  align-items: center;
}

.upload-card--failed .upload-card__icon {
  color: #ef4444;
}

.upload-card__info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upload-card__filename {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.upload-card__percentage {
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
}

.upload-card--failed .upload-card__percentage {
  color: #ef4444;
}

.upload-card__progress {
  height: 6px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.upload-card__progress-bar {
  height: 100%;
  background: #6366f1;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.upload-card--failed .upload-card__progress {
  background: rgba(239, 68, 68, 0.2);
}

.upload-card--failed .upload-card__progress-bar {
  background: #ef4444;
}

.upload-card__details {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.upload-card__error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(239, 68, 68, 0.2);
}

.upload-card__error-icon {
  color: #ef4444;
  display: flex;
  align-items: center;
}

.upload-card__error-message {
  flex: 1;
  font-size: 12px;
  color: #ef4444;
}

.upload-card__retry-btn {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #6366f1;
  background: transparent;
  border: 1px solid #6366f1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-card__retry-btn:hover {
  background: #6366f1;
  color: white;
}

.upload-card__cancel-btn {
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-card__cancel-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}
</style>
